import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Laptop, X } from 'lucide-react';
import { apiGetJson, apiDelete } from '../../../utils/api';

const QUERY_KEY = ['auth', 'trusted-devices'];

function formatDate(value) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysLeft(trustedUntil) {
  if (!trustedUntil) return null;
  const ms = new Date(trustedUntil).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

/**
 * The devices allowed to skip this user's 2FA code, and the controls to take
 * that back. Anything listed here can sign in with the password alone, so the
 * list has to be legible enough that an entry the user doesn't recognise stands
 * out: what it is, where it last signed in from, and when it expires.
 */
function TrustedDevices() {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  // isError matters more than usual on this screen: without it a failed load
  // falls through to "No trusted devices", which tells the user that nothing can
  // skip their 2FA when we simply do not know. That is the wrong way round for a
  // security surface to fail.
  //
  // (v5 removed onSuccess/onError from useQuery; read the state instead.)
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiGetJson('/auth/trusted_devices'),
    staleTime: 60 * 1000,
  });

  const revoke = useMutation({
    mutationFn: (id) => apiDelete(id ? `/auth/trusted_devices/${id}` : '/auth/trusted_devices'),
    onSuccess: () => {
      setError('');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => setError('Could not update your trusted devices. Please try again.'),
  });

  const devices = data?.devices || [];
  const trustDays = data?.trust_days || 30;

  // Switched off for the whole organization: say so rather than showing an
  // empty list that looks like the feature is simply unused.
  if (data && data.enabled === false) {
    return (
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h3 className="font-medium">Trusted Devices</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Your administrator requires a verification code on every sign-in, so no device can be trusted.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-medium">Trusted Devices</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            These devices skip the verification code for {trustDays} days. Your password is still required on every sign-in.
          </p>
        </div>
        {!isError && devices.length > 0 && (
          <button
            type="button"
            onClick={() => revoke.mutate(null)}
            disabled={revoke.isPending}
            className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50 whitespace-nowrap"
          >
            Remove all
          </button>
        )}
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {isLoading && (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      )}

      {isError && (
        <p className="mt-3 text-sm text-orange-700 dark:text-orange-400">
          We could not load your trusted devices, so this list may be incomplete. Reload to try again.
        </p>
      )}

      {!isLoading && !isError && devices.length === 0 && (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          No trusted devices. Every sign-in asks for a code.
        </p>
      )}

      <ul className="mt-3 space-y-2">
        {devices.map((device) => (
          <li
            key={device.id}
            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
          >
            <div className="flex items-start gap-3 min-w-0">
              <Laptop className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {device.device_label || 'Unknown device'}
                  {device.current && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      This device
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {device.last_location || device.last_ip || 'Location unknown'}
                  {device.last_used_at && ` · last used ${formatDate(device.last_used_at)}`}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Asks for a code again in {daysLeft(device.trusted_until)} day(s)
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => revoke.mutate(device.id)}
              disabled={revoke.isPending}
              aria-label={`Remove ${device.device_label || 'this device'}`}
              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default TrustedDevices;
